import React, { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const PHASE_META = {
    connect: { icon: 'fa-plug', label: 'Connexion FTP' },
    version: { icon: 'fa-circle-info', label: 'Détection version WP' },
    checksums: { icon: 'fa-cloud-arrow-down', label: 'Téléchargement checksums WP' },
    listing: { icon: 'fa-folder-tree', label: 'Lecture récursive' },
    'malware-paths': { icon: 'fa-bug', label: 'Chemins malveillants connus' },
    'well-known': { icon: 'fa-folder-magnifying-glass', label: 'Recherche .well-known' },
    'uploads-php': { icon: 'fa-file-code', label: '.php dans uploads' },
    'filename-scan': { icon: 'fa-magnifying-glass', label: 'Analyse motifs suspects' },
    md5: { icon: 'fa-fingerprint', label: 'Vérification MD5' }
};

function formatDuration(ms) {
    if (!ms || ms < 0) return '—';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return `${m}m ${rest}s`;
}

// Classify a single finding (category + file path) into a human-facing
// issue type — so the overview table can be filtered by issue kind.
const ISSUE_TYPES = {
    malware: { label: 'Malware', color: '#ef4444' },
    archive: { label: 'Fichiers archive (.zip/.rar/.7z)', color: '#ef4444' },
    wellKnown: { label: 'Dossier .well-known', color: '#ef4444' },
    pluginObf: { label: 'Plugin suspect', color: '#ef4444' },
    phpUploads: { label: '.php dans wp-content/uploads', color: '#ef4444' },
    phpCore: { label: 'PHP suspect', color: '#b91c1c' },
    shell: { label: 'Shell script (.sh)', color: '#991b1b' },
    themeSd: { label: 'Thème Solution Digitale', color: '#1dbab0' },
    modified: { label: 'Fichier modifié (MD5)', color: '#f97316' },
    added: { label: 'Fichier ajouté (hors core)', color: '#fac72a' },
    otherMal: { label: 'Autre', color: '#7c2d12' }
};

// Categories the user can pick when adding a suspect filename in the settings.
// Must stay in sync with ALLOWED_SUSPECT_CATEGORIES in server/app.js.
const SUSPECT_CATEGORY_CHOICES = ['malware', 'archive', 'phpCore', 'shell', 'wellKnown', 'pluginObf', 'themeSd', 'otherMal'];

function classifyIssue(path, category, suspectMap = null) {
    if (category === 'modified') return 'modified';
    if (category === 'added') return 'added';
    if (category === 'deleted') return null; // maintenance-only; hidden from overview
    if (category === 'uploadsPhp') return 'phpUploads';
    // category === 'malware' → first check the user-managed suspect list for a
    // basename match. Case-insensitive lookup, user-assigned category wins.
    const basename = ((path || '').split('/').pop() || '').toLowerCase();
    if (suspectMap && basename && suspectMap[basename]) return suspectMap[basename];
    // Fallback: path-based heuristics.
    const lower = (path || '').toLowerCase();
    if (/\.(zip|rar|7z|daf)$/.test(lower)) return 'archive';
    if (lower.includes('/.well-known') || lower.endsWith('/.well-knowm')) return 'wellKnown';
    if (/\/wp-content\/plugins\/[a-z]{6,12}(?:\/|$)/.test(lower) && !/\/wp-content\/plugins\/[a-z]{6,12}\/.+\/.+/.test(lower)) return 'pluginObf';
    if (lower.endsWith('.sh')) return 'shell';
    if (lower.endsWith('.php')) return 'phpCore';
    return 'otherMal';
}

function normalizeUrl(input) {
    let u = (input || '').trim();
    if (!u) return u;
    if (!/^https?:\/\//i.test(u)) u = 'https://' + u;
    return u.replace(/\/$/, '');
}

function StatusBadge({ status }) {
    // "warning" is only triggered by missing core WP files — often a false
    // positive / failed auto-update, not a security issue. We treat it as
    // SÉCURISÉ on this page so it doesn't alarm anyone.
    const map = {
        critical: { bg: '#ef4444', label: 'CRITIQUE' },
        warning: { bg: '#22c55e', label: 'SÉCURISÉ' },
        ok: { bg: '#22c55e', label: 'SÉCURISÉ' },
        error: { bg: '#6b7280', label: 'ERREUR' },
        skipped: { bg: '#94a3b8', label: 'IGNORÉ' }
    };
    const m = map[status] || { bg: '#94a3b8', label: (status || 'INCONNU').toUpperCase() };
    return (
        <span style={{
            background: m.bg,
            color: '#fff',
            padding: '6px 14px',
            borderRadius: 20,
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: 1
        }}>{m.label}</span>
    );
}

const SitesSecurite = () => {
    const [urlInput, setUrlInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [snapshot, setSnapshot] = useState({ globalRunning: false, paused: false, progress: {}, single: null, global: null });
    const [sites, setSites] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightIdx, setHighlightIdx] = useState(-1);
    const [reports, setReports] = useState([]);           // per-site security rows
    const [tableSearch, setTableSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all | critical | warning | ok
    const [issueTypeFilter, setIssueTypeFilter] = useState('all');
    const [issueDropdownOpen, setIssueDropdownOpen] = useState(false);
    const issueDropdownRef = useRef(null);
    const [sortKey, setSortKey] = useState('status');     // status | site | issues | lastScan
    const [sortAsc, setSortAsc] = useState(true);
    const [openRows, setOpenRows] = useState({});
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [allowlist, setAllowlist] = useState([]);
    const [newAllowPath, setNewAllowPath] = useState('');
    const [newAllowNote, setNewAllowNote] = useState('');
    const [allowlistError, setAllowlistError] = useState(null);
    const [settingsTab, setSettingsTab] = useState('allowlist'); // 'allowlist' | 'suspects'
    const [suspectNames, setSuspectNames] = useState([]);
    const [newSuspectName, setNewSuspectName] = useState('');
    const [newSuspectNote, setNewSuspectNote] = useState('');
    const [newSuspectCategory, setNewSuspectCategory] = useState('malware');
    const [newSuspectCaseSensitive, setNewSuspectCaseSensitive] = useState(false);
    const [suspectError, setSuspectError] = useState(null);
    const [suspectPaths, setSuspectPaths] = useState([]);
    const [newPathValue, setNewPathValue] = useState('');
    const [newPathNote, setNewPathNote] = useState('');
    const [newPathCategory, setNewPathCategory] = useState('malware');
    const [pathsError, setPathsError] = useState(null);
    const [forbiddenExts, setForbiddenExts] = useState([]);
    const [newExtValue, setNewExtValue] = useState('');
    const [newExtNote, setNewExtNote] = useState('');
    const [newExtCategory, setNewExtCategory] = useState('archive');
    const [extsError, setExtsError] = useState(null);
    // editRow: { type: 'allowlist'|'filename'|'path'|'ext', originalKey, value, note, category } | null
    const [editRow, setEditRow] = useState(null);
    const scanningUrlRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        document.title = 'Sécurité – Scan unitaire';
        fetchSnapshot();
        fetchSites();
        fetchReports();
        fetchAllowlist();
        fetchSuspectNames();
        fetchSuspectPaths();
        fetchForbiddenExts();
        const iv = setInterval(fetchSnapshot, 1200);
        const ivReports = setInterval(fetchReports, 20000);

        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
            if (issueDropdownRef.current && !issueDropdownRef.current.contains(e.target)) {
                setIssueDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            clearInterval(iv);
            clearInterval(ivReports);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchSites = () => {
        axios.post(`${API_BASE_URL}/get_published`)
            .then(res => {
                if (Array.isArray(res.data)) {
                    const list = res.data
                        .filter(s => s && s.url && s.is_active)
                        .map(s => ({ url: s.url, firm: s.firm || '' }));
                    setSites(list);
                }
            })
            .catch(err => console.error('Error fetching sites list:', err));
    };

    const fetchAllowlist = () => {
        axios.get(`${API_BASE_URL}/security_allowlist`)
            .then(r => { if (r.data.status === 'ok') setAllowlist(r.data.data || []); })
            .catch(err => console.error('Error fetching allowlist:', err));
    };

    // Forgive missing leading slash, strip any trailing slash, and collapse
    // consecutive internal slashes, so users can paste "/foo/bar.php",
    // "foo/bar.php", "/foo/bar.php/", or "//foo//bar.php/" and end up with
    // the canonical "/foo/bar.php".
    const normalizePathInput = (raw) => {
        let s = (raw || '').trim();
        if (!s) return '';
        if (!s.startsWith('/')) s = '/' + s;
        s = s.replace(/\/+/g, '/');
        s = s.replace(/\/+$/, '');
        return s || '/';
    };

    // Live onChange normalizer — keeps a single trailing slash the user is
    // still typing, but auto-adds a leading "/" and collapses double
    // slashes as they type so "//test" can never exist in the input.
    const normalizePathAsTyped = (raw) => {
        let s = raw || '';
        if (s && !s.startsWith('/')) s = '/' + s;
        s = s.replace(/\/{2,}/g, '/');
        return s;
    };

    const addToAllowlist = async () => {
        const trimmed = normalizePathInput(newAllowPath);
        if (!trimmed) { setAllowlistError('Chemin requis'); return; }
        setAllowlistError(null);
        try {
            const user = localStorage.getItem('user_id') || '';
            await axios.post(`${API_BASE_URL}/security_allowlist`, {
                file_path: trimmed,
                note: newAllowNote.trim(),
                created_by: user
            });
            setNewAllowPath('');
            setNewAllowNote('');
            fetchAllowlist();
            fetchReports(); // re-read so any freshly-allowlisted paths disappear from the overview
        } catch (err) {
            setAllowlistError(err.response?.data?.message || 'Erreur');
        }
    };

    const fetchSuspectNames = () => {
        axios.get(`${API_BASE_URL}/security_suspect_filenames`)
            .then(r => { if (r.data.status === 'ok') setSuspectNames(r.data.data || []); })
            .catch(err => console.error('Error fetching suspect filenames:', err));
    };

    const fetchSuspectPaths = () => {
        axios.get(`${API_BASE_URL}/security_suspect_paths`)
            .then(r => { if (r.data.status === 'ok') setSuspectPaths(r.data.data || []); })
            .catch(err => console.error('Error fetching suspect paths:', err));
    };

    const fetchForbiddenExts = () => {
        axios.get(`${API_BASE_URL}/security_forbidden_extensions`)
            .then(r => { if (r.data.status === 'ok') setForbiddenExts(r.data.data || []); })
            .catch(err => console.error('Error fetching forbidden extensions:', err));
    };

    const addSuspectPath = async () => {
        const trimmed = normalizePathInput(newPathValue);
        if (!trimmed) { setPathsError('Chemin requis'); return; }
        setPathsError(null);
        try {
            const user = localStorage.getItem('user_id') || '';
            await axios.post(`${API_BASE_URL}/security_suspect_paths`, {
                file_path: trimmed, note: newPathNote.trim(), category: newPathCategory, created_by: user
            });
            setNewPathValue(''); setNewPathNote(''); setNewPathCategory('malware');
            fetchSuspectPaths();
        } catch (err) {
            setPathsError(err.response?.data?.message || 'Erreur');
        }
    };
    const removeSuspectPath = async (p) => {
        if (!window.confirm(`Retirer "${p}" de la liste des chemins suspects ?`)) return;
        try {
            await axios.delete(`${API_BASE_URL}/security_suspect_paths`, { data: { file_path: p } });
            fetchSuspectPaths();
        } catch (err) {
            setPathsError(err.response?.data?.message || 'Erreur suppression');
        }
    };

    const addForbiddenExt = async () => {
        const trimmed = newExtValue.trim().toLowerCase();
        if (!trimmed) { setExtsError('Extension requise'); return; }
        setExtsError(null);
        try {
            const user = localStorage.getItem('user_id') || '';
            await axios.post(`${API_BASE_URL}/security_forbidden_extensions`, {
                ext: trimmed, note: newExtNote.trim(), category: newExtCategory, created_by: user
            });
            setNewExtValue(''); setNewExtNote(''); setNewExtCategory('archive');
            fetchForbiddenExts();
        } catch (err) {
            setExtsError(err.response?.data?.message || 'Erreur');
        }
    };
    const removeForbiddenExt = async (ext) => {
        if (!window.confirm(`Retirer "${ext}" de la liste des extensions interdites ?`)) return;
        try {
            await axios.delete(`${API_BASE_URL}/security_forbidden_extensions`, { data: { ext } });
            fetchForbiddenExts();
        } catch (err) {
            setExtsError(err.response?.data?.message || 'Erreur suppression');
        }
    };

    // ─── Inline edit of an existing rule row ──────────────────────────────
    // All four tables use their primary-key column as identifier. To "rename"
    // we just DELETE the old row + POST the new one (POST is upsert-safe).
    // If only the category/note changed, POST alone works.
    const beginEdit = (type, row) => {
        const key = type === 'filename' ? row.filename
            : type === 'ext' ? row.ext
                : row.file_path;
        setEditRow({
            type,
            originalKey: key,
            value: key,
            note: row.note || '',
            category: row.category || (type === 'ext' ? 'archive' : 'malware'),
            case_sensitive: !!row.case_sensitive
        });
    };
    const cancelEdit = () => setEditRow(null);
    const saveEdit = async () => {
        if (!editRow) return;
        const { type, originalKey, value, note, category, case_sensitive } = editRow;
        // Normalize path-like values the same way the "add" flow does.
        const trimmed = (type === 'path' || type === 'allowlist')
            ? normalizePathInput(value)
            : String(value || '').trim();
        if (!trimmed) return; // silently ignore empty save
        const user = localStorage.getItem('user_id') || '';
        try {
            // If the key changed, delete the old row first.
            if (trimmed !== originalKey) {
                if (type === 'filename') await axios.delete(`${API_BASE_URL}/security_suspect_filenames`, { data: { filename: originalKey } });
                else if (type === 'path') await axios.delete(`${API_BASE_URL}/security_suspect_paths`, { data: { file_path: originalKey } });
                else if (type === 'ext') await axios.delete(`${API_BASE_URL}/security_forbidden_extensions`, { data: { ext: originalKey } });
                else if (type === 'allowlist') await axios.delete(`${API_BASE_URL}/security_allowlist`, { data: { file_path: originalKey } });
            }
            // Upsert the new row.
            if (type === 'filename') {
                await axios.post(`${API_BASE_URL}/security_suspect_filenames`, { filename: trimmed, note, category, case_sensitive, created_by: user });
                fetchSuspectNames();
            } else if (type === 'path') {
                await axios.post(`${API_BASE_URL}/security_suspect_paths`, { file_path: trimmed, note, category, created_by: user });
                fetchSuspectPaths();
            } else if (type === 'ext') {
                await axios.post(`${API_BASE_URL}/security_forbidden_extensions`, { ext: trimmed, note, category, created_by: user });
                fetchForbiddenExts();
            } else if (type === 'allowlist') {
                await axios.post(`${API_BASE_URL}/security_allowlist`, { file_path: trimmed, note, created_by: user });
                fetchAllowlist();
            }
            fetchReports();
            setEditRow(null);
        } catch (err) {
            const msg = err.response?.data?.message || 'Erreur lors de la mise à jour';
            if (type === 'filename') setSuspectError(msg);
            else if (type === 'path') setPathsError(msg);
            else if (type === 'ext') setExtsError(msg);
            else setAllowlistError(msg);
        }
    };

    const addSuspectName = async () => {
        const trimmed = newSuspectName.trim();
        if (!trimmed) { setSuspectError('Nom de fichier requis'); return; }
        if (trimmed.includes('/') || trimmed.includes('\\')) {
            setSuspectError('Pas de chemin — seulement le nom du fichier'); return;
        }
        setSuspectError(null);
        try {
            const user = localStorage.getItem('user_id') || '';
            await axios.post(`${API_BASE_URL}/security_suspect_filenames`, {
                filename: trimmed,
                note: newSuspectNote.trim(),
                category: newSuspectCategory,
                case_sensitive: newSuspectCaseSensitive,
                created_by: user
            });
            setNewSuspectName('');
            setNewSuspectNote('');
            setNewSuspectCategory('malware');
            setNewSuspectCaseSensitive(false);
            fetchSuspectNames();
            fetchReports(); // re-classify with the new mapping
        } catch (err) {
            setSuspectError(err.response?.data?.message || 'Erreur');
        }
    };

    const removeSuspectName = async (name) => {
        if (!window.confirm(`Retirer "${name}" de la liste des noms suspects ?`)) return;
        try {
            await axios.delete(`${API_BASE_URL}/security_suspect_filenames`, { data: { filename: name } });
            fetchSuspectNames();
            fetchReports();
        } catch (err) {
            setSuspectError(err.response?.data?.message || 'Erreur suppression');
        }
    };

    const removeFromAllowlist = async (path) => {
        if (!window.confirm(`Retirer "${path}" de la whitelist ?`)) return;
        try {
            await axios.delete(`${API_BASE_URL}/security_allowlist`, { data: { file_path: path } });
            fetchAllowlist();
            fetchReports();
        } catch (err) {
            setAllowlistError(err.response?.data?.message || 'Erreur suppression');
        }
    };

    const fetchReports = () => {
        axios.get(`${API_BASE_URL}/security_scan_status`).catch(() => { }); // noop, keeps snapshot warm
        // Build a case-insensitive basename → user-assigned category map from
        // the current suspectNames list. Falls back to path heuristics for
        // names not in the map.
        const suspectMap = {};
        for (const s of suspectNames) {
            if (s && s.filename) suspectMap[s.filename.toLowerCase()] = s.category || 'malware';
        }
        axios.get(`${API_BASE_URL}/get_security_report`)
            .then(res => {
                if (res.data.status === 'ok' && Array.isArray(res.data.data)) {
                    const parsed = res.data.data.map(row => {
                        let d = {};
                        try { d = JSON.parse(row.details || '{}'); } catch { d = {}; }
                        // Safety-net dedup: a file reported in uploadsPhp must
                        // never also appear in the generic malware list.
                        // Cleans up rows written before the scanner's dedup fix.
                        if (Array.isArray(d.uploadsPhp) && Array.isArray(d.malware)) {
                            const upSet = new Set(d.uploadsPhp);
                            d.malware = d.malware.filter(f => !upSet.has(f));
                        }
                        const counts = {
                            malware: (d.malware || []).length,
                            uploadsPhp: (d.uploadsPhp || []).length,
                            modified: (d.modified || []).length,
                            added: (d.added || []).length,
                            deleted: (d.deleted || []).length
                        };
                        counts.total = counts.malware + counts.uploadsPhp + counts.modified + counts.added + counts.deleted;
                        counts.security = counts.malware + counts.uploadsPhp + counts.modified + counts.added;
                        const issueTypes = new Set();
                        // Track both: the SET of types on this site (for filtering)
                        // and the PER-TYPE FILE COUNT on this site (so dropdown totals
                        // match the "Anomalies de sécurité" stat card).
                        const issueTypeCounts = {};
                        const addType = (t) => {
                            if (!t) return;
                            issueTypes.add(t);
                            issueTypeCounts[t] = (issueTypeCounts[t] || 0) + 1;
                        };
                        for (const f of (d.malware || [])) addType(classifyIssue(f, 'malware', suspectMap));
                        for (const f of (d.uploadsPhp || [])) addType(classifyIssue(f, 'uploadsPhp', suspectMap));
                        for (const f of (d.modified || [])) addType(classifyIssue(f, 'modified', suspectMap));
                        for (const f of (d.added || [])) addType(classifyIssue(f, 'added', suspectMap));
                        return { ...row, parsedDetails: d, counts, issueTypes: Array.from(issueTypes), issueTypeCounts };
                    });
                    setReports(parsed);
                }
            })
            .catch(err => console.error('Error fetching security reports:', err));
    };

    const matches = useMemo(() => {
        const q = urlInput.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
        if (!q) return [];
        return sites
            .filter(s => {
                const host = s.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                return host.includes(q) || (s.firm || '').toLowerCase().includes(q);
            })
            .slice(0, 8);
    }, [urlInput, sites]);

    // Cache the last serialized snapshot so we only trigger a React re-render
    // when the scan state actually changed. Otherwise a 1.2s poll that returns
    // the same "no scan running" payload would re-render the whole page 50×/min
    // and recompute every derived value.
    const lastSnapshotKeyRef = useRef('');
    const fetchSnapshot = useCallback(() => {
        axios.get(`${API_BASE_URL}/security_scan_status`)
            .then(r => {
                const next = r.data || {};
                const key = JSON.stringify([
                    !!next.globalRunning,
                    !!next.paused,
                    next.single || null,
                    next.global || null,
                    next.progress || {}
                ]);
                if (key === lastSnapshotKeyRef.current) return; // no-op
                lastSnapshotKeyRef.current = key;
                setSnapshot(next);
            })
            .catch(() => { });
    }, []);

    const handleScan = async (forcedUrl = null) => {
        // Only accept a string as forcedUrl — button onClick handlers pass the
        // MouseEvent as the first arg, which would otherwise blow up normalizeUrl.
        const urlSource = (typeof forcedUrl === 'string' && forcedUrl) ? forcedUrl : urlInput;
        const url = normalizeUrl(urlSource);
        if (!url) {
            setError('Veuillez entrer une URL');
            return;
        }
        setLoading(true);
        setResult(null);
        setError(null);
        scanningUrlRef.current = url;
        try {
            const res = await axios.post(`${API_BASE_URL}/run_single_security_scan`, { url });
            if (res.data.status === 'ok') {
                setResult(res.data);
            } else if (res.data.status === 'not_found') {
                setError("Cette URL n'est pas dans la liste des sites gérés.");
            } else {
                setError(res.data.message || 'Erreur inconnue');
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setError("Cette URL n'est pas dans la liste des sites gérés.");
            } else {
                setError(err.response?.data?.message || err.message || 'Erreur lors du scan');
            }
        } finally {
            setLoading(false);
            scanningUrlRef.current = null;
            fetchSnapshot();
            fetchReports();
        }
    };

    // ── Derived data for the per-site overview table ──
    const sitesMap = useMemo(() => {
        const m = {};
        for (const s of sites) m[s.url] = s;
        return m;
    }, [sites]);

    const tableStats = useMemo(() => {
        const s = { total: reports.length, critical: 0, ok: 0, issues: 0, withMalware: 0 };
        for (const r of reports) {
            if (r.status === 'critical') s.critical++;
            else s.ok++;
            const c = r.counts || {};
            s.issues += (c.malware || 0) + (c.uploadsPhp || 0) + (c.modified || 0) + (c.added || 0);
            if ((c.malware || 0) > 0 || (c.uploadsPhp || 0) > 0) s.withMalware++;
        }
        return s;
    }, [reports]);

    const availableIssueTypes = useMemo(() => {
        const fileCounts = {};
        const siteCounts = {};
        for (const r of reports) {
            for (const [t, n] of Object.entries(r.issueTypeCounts || {})) {
                fileCounts[t] = (fileCounts[t] || 0) + n;
            }
            for (const t of (r.issueTypes || [])) {
                siteCounts[t] = (siteCounts[t] || 0) + 1;
            }
        }
        return Object.keys(ISSUE_TYPES)
            .filter(k => fileCounts[k])
            .map(k => ({ key: k, ...ISSUE_TYPES[k], fileCount: fileCounts[k], siteCount: siteCounts[k] || 0 }));
    }, [reports]);

    const filteredReports = useMemo(() => {
        const q = tableSearch.trim().toLowerCase();
        const statusRank = { critical: 0, warning: 1, ok: 2, skipped: 3, error: 4 };
        return reports
            .filter(r => {
                const effectiveStatus = r.status === 'warning' ? 'ok' : r.status;
                if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
                if (issueTypeFilter !== 'all' && !(r.issueTypes || []).includes(issueTypeFilter)) return false;
                if (!q) return true;
                const host = (r.website || '').toLowerCase();
                const firm = (sitesMap[r.website]?.firm || '').toLowerCase();
                return host.includes(q) || firm.includes(q);
            })
            .sort((a, b) => {
                let cmp = 0;
                if (sortKey === 'status') cmp = (statusRank[a.status] ?? 99) - (statusRank[b.status] ?? 99);
                else if (sortKey === 'site') cmp = (a.website || '').localeCompare(b.website || '');
                else if (sortKey === 'issues') {
                    cmp = (a.counts?.security || 0) - (b.counts?.security || 0);
                    if (cmp === 0) cmp = (a.website || '').localeCompare(b.website || '');
                }
                else if (sortKey === 'lastScan') cmp = new Date(a.last_scan || 0) - new Date(b.last_scan || 0);
                return sortAsc ? cmp : -cmp;
            });
    }, [reports, tableSearch, statusFilter, issueTypeFilter, sortKey, sortAsc, sitesMap]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortAsc(a => !a);
        else { setSortKey(key); setSortAsc(key === 'issues' ? false : true); }
    };

    const toggleRow = (url) => setOpenRows(o => ({ ...o, [url]: !o[url] }));

    const formatScanDate = (d) => {
        if (!d) return '—';
        try {
            return new Date(d).toLocaleString('fr-CH', {
                day: '2-digit', month: '2-digit', year: '2-digit',
                hour: '2-digit', minute: '2-digit'
            });
        } catch { return '—'; }
    };

    const startScanFromTable = (url) => {
        setUrlInput(url);
        setOpenRows(o => ({ ...o, [url]: true })); // auto-expand so live progress is visible
        handleScan(url);
        // Prime the snapshot cache right away so we don't wait up to 1.2s for the
        // polling tick to surface the first "Connexion FTP…" phase.
        setTimeout(fetchSnapshot, 150);
        setTimeout(fetchSnapshot, 500);
        // No scroll — keep the user on the row they just clicked.
    };

    // Renders the in-place edit inputs (value + optional category + note +
    // save/cancel) for any of the four rule tabs. Used inside each row when
    // editRow.type + originalKey match.
    const renderEditInputs = (showCategory) => {
        if (!editRow) return null;
        return (
            <>
                <input
                    className="ss-input"
                    type="text"
                    autoFocus
                    value={editRow.value}
                    onChange={e => {
                        const raw = e.target.value;
                        const next = (editRow.type === 'path' || editRow.type === 'allowlist')
                            ? normalizePathAsTyped(raw)
                            : raw;
                        setEditRow(er => ({ ...er, value: next }));
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    style={{ flex: 2, minWidth: 200, fontSize: 13, padding: '6px 10px' }}
                />
                {showCategory && (
                    <select
                        className="ss-input"
                        value={editRow.category}
                        onChange={e => setEditRow(er => ({ ...er, category: e.target.value }))}
                        style={{ minWidth: 150, cursor: 'pointer', fontSize: 13, padding: '6px 10px' }}
                    >
                        {SUSPECT_CATEGORY_CHOICES.map(k => (
                            <option key={k} value={k}>{ISSUE_TYPES[k]?.label || k}</option>
                        ))}
                    </select>
                )}
                <input
                    className="ss-input"
                    type="text"
                    placeholder="Note"
                    value={editRow.note}
                    onChange={e => setEditRow(er => ({ ...er, note: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    style={{ flex: 1, minWidth: 140, fontSize: 13, padding: '6px 10px' }}
                />
                {editRow.type === 'filename' && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }} title="Si coché, le nom doit correspondre exactement à la casse près (ex : wp.php ne matchera pas WP.php)">
                        <input
                            type="checkbox"
                            checked={!!editRow.case_sensitive}
                            onChange={e => setEditRow(er => ({ ...er, case_sensitive: e.target.checked }))}
                            style={{ cursor: 'pointer' }}
                        />
                        Aa
                    </label>
                )}
                <button
                    onClick={saveEdit}
                    className="ss-btn-primary"
                    style={{ padding: '6px 12px', fontSize: 12 }}
                    title="Enregistrer (Entrée)"
                >
                    <i className="fa-solid fa-check" />
                </button>
                <button
                    onClick={cancelEdit}
                    className="ss-btn-secondary"
                    style={{ padding: '6px 12px', fontSize: 12 }}
                    title="Annuler (Échap)"
                >
                    <i className="fa-solid fa-xmark" />
                </button>
            </>
        );
    };

    const exportCsv = () => {
        if (filteredReports.length === 0) return;
        const csvEscape = (v) => {
            const s = (v === null || v === undefined) ? '' : String(v);
            return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const header = [
            'Statut', 'Site', 'Client', 'Malware', '.php uploads',
            'Modifiés', 'Ajoutés', 'Total anomalies sécurité',
            "Types d'anomalies", 'Dernier scan'
        ];
        const rows = filteredReports.map(r => {
            const c = r.counts || {};
            const displayStatus = r.status === 'warning' ? 'ok' : (r.status || '');
            const types = (r.issueTypes || []).map(t => ISSUE_TYPES[t]?.label || t).join(', ');
            return [
                displayStatus,
                r.website || '',
                sitesMap[r.website]?.firm || '',
                c.malware || 0,
                c.uploadsPhp || 0,
                c.modified || 0,
                c.added || 0,
                c.security || 0,
                types,
                r.last_scan ? new Date(r.last_scan).toISOString().replace('T', ' ').slice(0, 16) : ''
            ].map(csvEscape).join(',');
        });
        const csv = '﻿' + [header.map(csvEscape).join(','), ...rows].join('\n'); // BOM for Excel
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
        a.href = url;
        a.download = `securite_${stamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Figure out what to show in the live panel.
    // Priority: progress for the URL we just submitted → any single-scan progress
    // → aggregate of global scan progress.
    const progressMap = snapshot.progress || {};
    const scanningUrl = scanningUrlRef.current || (snapshot.single && snapshot.single.siteUrl);
    const singleProgress = scanningUrl ? progressMap[scanningUrl] : null;
    const globalProgressList = Object.entries(progressMap).filter(([, v]) => v.kind === 'global');

    const renderResult = () => {
        if (!result || !result.result) return null;
        const r = result.result;

        const section = (title, items, color) => {
            if (!items || items.length === 0) return null;
            return (
                <div style={{
                    background: '#fff',
                    borderLeft: `4px solid ${color}`,
                    borderRadius: 10,
                    padding: '14px 18px',
                    marginTop: 12
                }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#1e293b' }}>
                        {title} <span style={{ color: '#64748b', fontWeight: 500 }}>({items.length})</span>
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151' }}>
                        {items.slice(0, 60).map((f, i) => (
                            <li key={i} style={{ wordBreak: 'break-all', lineHeight: 1.7 }}>
                                <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>{f}</code>
                            </li>
                        ))}
                        {items.length > 60 && <li><em style={{ color: '#64748b' }}>… et {items.length - 60} de plus</em></li>}
                    </ul>
                </div>
            );
        };

        return (
            <div className="ss-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a', wordBreak: 'break-all' }}>{result.website}</h3>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
                            Scan unitaire terminé — version WP détectée : <strong>{r.version || '—'}</strong>
                        </p>
                    </div>
                    <StatusBadge status={r.status} />
                </div>
                {section('Malware & code suspect', r.malware, '#ef4444')}
                {section('.php dans wp-content/uploads', r.uploadsPhp, '#ef4444')}
                {section('Fichiers modifiés (MD5 différent)', r.modified, '#f97316')}
                {section('Fichiers ajoutés (hors clean install WP)', r.added, '#fac72a')}
                {section('Fichiers supprimés (devraient être présents)', r.deleted, '#94a3b8')}
                {r.status === 'ok' && (
                    <div style={{
                        background: '#ecfdf5',
                        borderLeft: '4px solid #22c55e',
                        borderRadius: 10,
                        padding: '14px 18px',
                        marginTop: 12,
                        color: '#065f46',
                        fontSize: 14
                    }}>
                        <i className="fa-solid fa-check-circle" style={{ marginRight: 8 }} />
                        Aucune anomalie détectée. Les fichiers du cœur correspondent aux signatures officielles WordPress.
                    </div>
                )}
                <div style={{ marginTop: 14 }}>
                    <NavLink
                        to={`/rapport-securite/${encodeURIComponent(result.website)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ss-btn-secondary"
                        style={{ textDecoration: 'none' }}
                    >
                        <i className="fa-solid fa-up-right-from-square" style={{ marginRight: 6 }} />
                        Ouvrir le rapport complet
                    </NavLink>
                </div>
            </div>
        );
    };

    const renderLivePanel = () => {
        // Show for: active single scan OR global scan running
        const showSingle = !!singleProgress;
        const showGlobal = snapshot.globalRunning || globalProgressList.length > 0;
        if (!showSingle && !showGlobal) return null;

        return (
            <div className="ss-card" style={{ borderLeft: `5px solid ${showSingle ? '#3b82f6' : '#fac72a'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
                    <h4 style={{ margin: 0, fontSize: 14, color: '#1e293b', fontWeight: 700 }}>
                        <i className="fa-solid fa-spinner fa-spin" style={{ color: showSingle ? '#3b82f6' : '#fac72a', marginRight: 10 }} />
                        {showSingle ? 'Scan unitaire en cours' : 'Scan global en cours (en arrière-plan)'}
                    </h4>
                    {snapshot.paused && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#854d0e', background: '#fef9c3', padding: '3px 10px', borderRadius: 20 }}>
                            <i className="fa-solid fa-pause" style={{ marginRight: 5 }} />
                            Scan global en pause
                        </span>
                    )}
                </div>

                {showSingle && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <i className={`fa-solid ${PHASE_META[singleProgress.phase]?.icon || 'fa-gears'}`} style={{ color: '#3b82f6', fontSize: 14 }} />
                            <strong style={{ color: '#0f172a', fontSize: 13 }}>
                                {PHASE_META[singleProgress.phase]?.label || singleProgress.phase || '…'}
                            </strong>
                            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
                                {formatDuration(Date.now() - (singleProgress.startedAt || Date.now()))}
                            </span>
                        </div>
                        <div style={{ fontSize: 13, color: '#475569', wordBreak: 'break-all', lineHeight: 1.6 }}>
                            {singleProgress.message}
                        </div>
                        {singleProgress.path && (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                <i className="fa-solid fa-folder" style={{ marginRight: 6, color: '#94a3b8' }} />
                                {singleProgress.path}
                            </div>
                        )}
                    </div>
                )}

                {showGlobal && snapshot.global && (
                    <div style={{ marginTop: showSingle ? 12 : 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', marginBottom: 6 }}>
                            <span><strong>Scan global :</strong> {snapshot.global.completedSites}/{snapshot.global.totalSites} sites</span>
                            <span>{Math.round((snapshot.global.completedSites / (snapshot.global.totalSites || 1)) * 100)}%</span>
                        </div>
                        <div style={{ width: '100%', height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
                            <div style={{
                                width: `${(snapshot.global.completedSites / (snapshot.global.totalSites || 1)) * 100}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #fac72a, #f59e0b)',
                                transition: 'width 0.4s'
                            }} />
                        </div>
                        {globalProgressList.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                                {globalProgressList.slice(0, 8).map(([url, p]) => (
                                    <div key={url} style={{
                                        background: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 8,
                                        padding: '8px 10px',
                                        fontSize: 11
                                    }}>
                                        <div style={{ color: '#0f172a', fontWeight: 600, marginBottom: 3, wordBreak: 'break-all' }}>
                                            <i className={`fa-solid ${PHASE_META[p.phase]?.icon || 'fa-gears'}`} style={{ color: '#3b82f6', marginRight: 6 }} />
                                            {url.replace(/^https?:\/\//, '')}
                                        </div>
                                        <div style={{ color: '#64748b', wordBreak: 'break-all' }}>
                                            {PHASE_META[p.phase]?.label || p.phase}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Fragment>
            <style>{`
                .ss-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
                    padding: 22px 24px;
                    margin-bottom: 22px;
                }
                /* Scroll performance: skip rendering rows that aren't in the
                   viewport. Browsers that support content-visibility reserve
                   56px of space via contain-intrinsic-size and only paint
                   the row when it's near the visible area. */
                .ss-row {
                    content-visibility: auto;
                    contain-intrinsic-size: 0 56px;
                }
                .ss-stat-card {
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                    color: #fff;
                    border-radius: 14px;
                    padding: 18px 22px;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    flex: 1;
                    min-width: 140px;
                }
                .ss-stat-card.amber  { background: linear-gradient(135deg, #fac72a, #f59e0b); color: #422006; }
                .ss-stat-card.green  { background: linear-gradient(135deg, #22c55e, #16a34a); }
                .ss-stat-card.slate  { background: linear-gradient(135deg, #64748b, #475569); }
                .ss-input {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 9px;
                    padding: 10px 14px;
                    font-size: 14px;
                    outline: none;
                    background: #f8fafc;
                    transition: border-color 0.2s, background 0.2s;
                    color: #1e293b;
                }
                .ss-input:focus { border-color: #3b82f6; background: #fff; }
                .ss-btn-primary {
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                    color: #fff;
                    border: none;
                    border-radius: 9px;
                    padding: 10px 20px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: opacity 0.2s, transform 0.15s;
                }
                .ss-btn-primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
                .ss-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
                .ss-btn-secondary {
                    background: #f1f5f9;
                    color: #334155;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 9px;
                    padding: 9px 16px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    transition: background 0.2s;
                }
                .ss-btn-secondary:hover { background: #e2e8f0; }
                .ss-cron-badge {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: #ecfdf5; color: #065f46;
                    border: 1px solid #6ee7b7;
                    border-radius: 20px;
                    padding: 4px 12px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .ss-pulse { animation: sspulse 2s infinite; }
                @keyframes sspulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
            `}</style>

            <div>
                <div style={{ flexDirection: 'column', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                        <i className="fa-solid fa-shield-halved" style={{ color: '#fac72a', marginRight: 10 }} />
                        Sécurité — Scan unitaire
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b', textAlign: 'center', maxWidth: 700 }}>
                        Lance un scan de sécurité immédiat sur un site du parc. Si le scan global est en cours,
                        il est mis en pause le temps de votre scan puis repris automatiquement.
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexDirection: 'column' }}>
                        <span className="ss-cron-badge">
                            <span className="ss-pulse" style={{ width: 7, height: 7, background: snapshot.globalRunning ? '#3b82f6' : '#22c55e', borderRadius: '50%' }} />
                            {snapshot.globalRunning ? 'Scan global en cours' : 'Cron toutes les 6h'}
                        </span>
                    </div>
                </div>

                {/* Input card */}
                <div className="ss-card">
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <div ref={dropdownRef} style={{ position: 'relative', flex: 1, minWidth: 280 }}>
                            <i className="fa-solid fa-globe" style={{
                                position: 'absolute', left: 14, top: 14, color: '#94a3b8', zIndex: 1
                            }} />
                            <input
                                type="text"
                                value={urlInput}
                                onChange={(e) => { setUrlInput(e.target.value); setShowDropdown(true); setHighlightIdx(-1); }}
                                onFocus={() => setShowDropdown(true)}
                                onKeyDown={(e) => {
                                    if (!showDropdown || matches.length === 0) {
                                        if (e.key === 'Enter' && !loading) handleScan();
                                        return;
                                    }
                                    if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        setHighlightIdx(i => Math.min(matches.length - 1, i + 1));
                                    } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        setHighlightIdx(i => Math.max(0, i - 1));
                                    } else if (e.key === 'Enter') {
                                        if (highlightIdx >= 0 && matches[highlightIdx]) {
                                            setUrlInput(matches[highlightIdx].url);
                                            setShowDropdown(false);
                                            setHighlightIdx(-1);
                                        } else if (!loading) {
                                            handleScan();
                                        }
                                    } else if (e.key === 'Escape') {
                                        setShowDropdown(false);
                                    }
                                }}
                                placeholder="https://exemple.ch"
                                disabled={loading}
                                className="ss-input"
                                autoComplete="off"
                                style={{ width: '100%', paddingLeft: 38 }}
                            />
                            {showDropdown && matches.length > 0 && (
                                <div style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    right: 0,
                                    background: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 9,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                    zIndex: 20,
                                    maxHeight: 280,
                                    overflowY: 'auto'
                                }}>
                                    {matches.map((s, i) => {
                                        const host = s.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
                                        const active = i === highlightIdx;
                                        return (
                                            <div
                                                key={s.url}
                                                onMouseEnter={() => setHighlightIdx(i)}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    setUrlInput(s.url);
                                                    setShowDropdown(false);
                                                    setHighlightIdx(-1);
                                                }}
                                                style={{
                                                    padding: '10px 14px',
                                                    cursor: 'pointer',
                                                    background: active ? '#f1f5f9' : '#fff',
                                                    borderBottom: i === matches.length - 1 ? 'none' : '1px solid #f1f5f9',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10
                                                }}
                                            >
                                                <i className="fa-solid fa-globe" style={{ color: '#94a3b8', fontSize: 12 }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {host}
                                                    </div>
                                                    {s.firm && (
                                                        <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {s.firm}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleScan}
                            disabled={loading || !urlInput.trim()}
                            className="ss-btn-primary"
                        >
                            <i className={`fa-solid ${loading ? 'fa-circle-notch fa-spin' : 'fa-shield-halved'}`} />
                            {loading ? 'Scan en cours…' : 'Lancer le scan'}
                        </button>
                    </div>
                    {error && (
                        <div style={{
                            marginTop: 12,
                            padding: '10px 14px',
                            background: '#fee2e2',
                            borderLeft: '4px solid #ef4444',
                            color: '#991b1b',
                            borderRadius: 8,
                            fontSize: 13
                        }}>
                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }} />
                            {error}
                        </div>
                    )}
                </div>

                {/* Live "what's happening now" */}
                {renderLivePanel()}

                {/* Result */}
                {renderResult()}

                {/* ── Settings: allowlist ── */}
                <div className="ss-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <button
                        onClick={() => setSettingsOpen(o => !o)}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: '16px 24px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <i className="fa-solid fa-gear" style={{ color: '#64748b', fontSize: 16 }} />
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Paramètres de détection</div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                    {allowlist.length} exclu{allowlist.length !== 1 ? 's' : ''} · {suspectNames.length} nom{suspectNames.length !== 1 ? 's' : ''} · {suspectPaths.length} chemin{suspectPaths.length !== 1 ? 's' : ''} · {forbiddenExts.length} ext
                                </div>
                            </div>
                        </div>
                        <i className={`fa-solid ${settingsOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ color: '#94a3b8', fontSize: 13 }} />
                    </button>

                    {settingsOpen && (
                        <div style={{ borderTop: '1px solid #f1f5f9', padding: '18px 24px' }}>
                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', marginBottom: 18 }}>
                                {[
                                    { key: 'allowlist', label: 'Fichiers exclus', icon: 'fa-shield', count: allowlist.length, color: '#22c55e' },
                                    { key: 'suspects', label: 'Fichiers suspects', icon: 'fa-bug', count: suspectNames.length, color: '#ef4444' },
                                    { key: 'paths', label: 'Dossiers suspects', icon: 'fa-folder-tree', count: suspectPaths.length, color: '#f97316' },
                                    { key: 'exts', label: 'Extensions', icon: 'fa-file-zipper', count: forbiddenExts.length, color: '#7c3aed' }
                                ].map(t => {
                                    const active = settingsTab === t.key;
                                    return (
                                        <button
                                            key={t.key}
                                            onClick={() => setSettingsTab(t.key)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: '10px 18px',
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: active ? '#0f172a' : '#64748b',
                                                borderBottom: `2.5px solid ${active ? t.color : 'transparent'}`,
                                                marginBottom: -1,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 8
                                            }}
                                        >
                                            <i className={`fa-solid ${t.icon}`} style={{ color: t.color, fontSize: 12 }} />
                                            {t.label}
                                            <span style={{
                                                background: active ? t.color : '#e2e8f0',
                                                color: active ? '#fff' : '#64748b',
                                                borderRadius: 10,
                                                padding: '1px 7px',
                                                fontSize: 10,
                                                fontWeight: 700
                                            }}>{t.count}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {settingsTab === 'allowlist' && (
                                <>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
                                        Entrez un chemin <strong>exact</strong> (sensible à la casse, pas de jokers). Le <code>/</code> initial est ajouté automatiquement si oublié, le <code>/</code> final est retiré.
                                        Appliqué à toutes les catégories de détection et à tous les types de scan (cron et manuel).
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                                        <input
                                            className="ss-input"
                                            type="text"
                                            placeholder="/wp-content/uploads/mon-plugin/index.php"
                                            value={newAllowPath}
                                            onChange={e => setNewAllowPath(normalizePathAsTyped(e.target.value))}
                                            onKeyDown={e => { if (e.key === 'Enter') addToAllowlist(); }}
                                            style={{ flex: 2, minWidth: 260 }}
                                        />
                                        <input
                                            className="ss-input"
                                            type="text"
                                            placeholder="Note (optionnel)"
                                            value={newAllowNote}
                                            onChange={e => setNewAllowNote(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') addToAllowlist(); }}
                                            style={{ flex: 1, minWidth: 180 }}
                                        />
                                        <button onClick={addToAllowlist} className="ss-btn-primary">
                                            <i className="fa-solid fa-plus" />
                                            Ajouter
                                        </button>
                                    </div>
                                    {allowlistError && (
                                        <div style={{ fontSize: 12, color: '#991b1b', background: '#fee2e2', borderLeft: '3px solid #ef4444', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>
                                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                                            {allowlistError}
                                        </div>
                                    )}

                                    {allowlist.length === 0 ? (
                                        <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '18px 0' }}>
                                            Aucun chemin dans la whitelist.
                                        </div>
                                    ) : (
                                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                            {allowlist.map((a, i) => {
                                                const isEditing = editRow && editRow.type === 'allowlist' && editRow.originalKey === a.file_path;
                                                return (
                                                    <div key={a.file_path} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 12,
                                                        padding: '10px 14px',
                                                        borderBottom: i === allowlist.length - 1 ? 'none' : '1px solid #f1f5f9',
                                                        fontSize: 13,
                                                        background: isEditing ? '#f1f5f9' : 'transparent'
                                                    }}>
                                                        <i className="fa-solid fa-shield" style={{ color: '#22c55e', fontSize: 12 }} />
                                                        {isEditing ? renderEditInputs(false) : (
                                                            <>
                                                                <div
                                                                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                                                    onClick={() => beginEdit('allowlist', a)}
                                                                    title="Cliquer pour modifier"
                                                                >
                                                                    <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontSize: 12, wordBreak: 'break-all' }}>
                                                                        {a.file_path}
                                                                    </code>
                                                                    {a.note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{a.note}</div>}
                                                                </div>
                                                                <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                                                    {a.created_at ? new Date(a.created_at).toLocaleDateString('fr-CH') : ''}
                                                                </div>
                                                                <button
                                                                    onClick={() => beginEdit('allowlist', a)}
                                                                    className="ss-btn-secondary"
                                                                    style={{ padding: '4px 10px', fontSize: 11 }}
                                                                    title="Modifier"
                                                                >
                                                                    <i className="fa-solid fa-pen" />
                                                                </button>
                                                                <button
                                                                    onClick={() => removeFromAllowlist(a.file_path)}
                                                                    className="ss-btn-secondary"
                                                                    style={{ padding: '4px 10px', fontSize: 11, color: '#991b1b' }}
                                                                    title="Retirer"
                                                                >
                                                                    <i className="fa-solid fa-xmark" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}

                            {settingsTab === 'suspects' && (
                                <>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
                                        Entrez un <strong>nom de fichier</strong> seul (sans chemin). Tout fichier dont le nom correspond
                                        (<strong>insensible à la casse</strong> — <code>Backup.php</code>, <code>BACKUP.PHP</code>, <code>backup.php</code> matchent la même entrée)
                                        sera marqué malware <strong>où qu'il se trouve</strong> sur le site. Appliqué au cron et aux scans manuels.
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                                        <input
                                            className="ss-input"
                                            type="text"
                                            placeholder="backup.php"
                                            value={newSuspectName}
                                            onChange={e => setNewSuspectName(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') addSuspectName(); }}
                                            style={{ flex: 2, minWidth: 220 }}
                                        />
                                        <select
                                            className="ss-input"
                                            value={newSuspectCategory}
                                            onChange={e => setNewSuspectCategory(e.target.value)}
                                            style={{ minWidth: 170, cursor: 'pointer' }}
                                            title="Type d'anomalie à rapporter quand ce nom est trouvé"
                                        >
                                            {SUSPECT_CATEGORY_CHOICES.map(key => (
                                                <option key={key} value={key}>
                                                    {ISSUE_TYPES[key]?.label || key}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            className="ss-input"
                                            type="text"
                                            placeholder="Note (optionnel)"
                                            value={newSuspectNote}
                                            onChange={e => setNewSuspectNote(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') addSuspectName(); }}
                                            style={{ flex: 1, minWidth: 160 }}
                                        />
                                        <button onClick={addSuspectName} className="ss-btn-primary">
                                            <i className="fa-solid fa-plus" />
                                            Ajouter
                                        </button>
                                    </div>
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569', cursor: 'pointer', marginBottom: 12 }}>
                                        <input
                                            type="checkbox"
                                            checked={newSuspectCaseSensitive}
                                            onChange={e => setNewSuspectCaseSensitive(e.target.checked)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        Correspondance <strong>sensible à la casse</strong>
                                        <span style={{ color: '#94a3b8' }}>
                                            — utile pour <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>wp.php</code> qui ne doit pas matcher <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>WP.php</code> (ex : WP Mail SMTP)
                                        </span>
                                    </label>
                                    {suspectError && (
                                        <div style={{ fontSize: 12, color: '#991b1b', background: '#fee2e2', borderLeft: '3px solid #ef4444', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>
                                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                                            {suspectError}
                                        </div>
                                    )}

                                    {suspectNames.length === 0 ? (
                                        <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '18px 0' }}>
                                            Aucun nom ajouté par l'utilisateur. La liste par défaut du scanner reste active en interne.
                                        </div>
                                    ) : (
                                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                            {suspectNames.map((s, i) => {
                                                const catKey = s.category || 'malware';
                                                const catMeta = ISSUE_TYPES[catKey] || ISSUE_TYPES.malware;
                                                const isEditing = editRow && editRow.type === 'filename' && editRow.originalKey === s.filename;
                                                return (
                                                    <div key={s.filename} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 12,
                                                        padding: '10px 14px',
                                                        borderBottom: i === suspectNames.length - 1 ? 'none' : '1px solid #f1f5f9',
                                                        fontSize: 13,
                                                        background: isEditing ? '#f1f5f9' : 'transparent'
                                                    }}>
                                                        <i className="fa-solid fa-bug" style={{ color: catMeta.color, fontSize: 12 }} />
                                                        {isEditing ? renderEditInputs(true) : (
                                                            <>
                                                                <div
                                                                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                                                    onClick={() => beginEdit('filename', s)}
                                                                    title="Cliquer pour modifier"
                                                                >
                                                                    <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontSize: 12, wordBreak: 'break-all' }}>
                                                                        {s.filename}
                                                                    </code>
                                                                    {s.note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{s.note}</div>}
                                                                </div>
                                                                <span style={{ background: '#fff', color: catMeta.color, border: `1px solid ${catMeta.color}44`, padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                    {catMeta.label}
                                                                </span>
                                                                {s.case_sensitive ? (
                                                                    <span title="Correspondance sensible à la casse" style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                        Aa
                                                                    </span>
                                                                ) : null}
                                                                <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                                                    {s.created_at ? new Date(s.created_at).toLocaleDateString('fr-CH') : ''}
                                                                </div>
                                                                <button onClick={() => beginEdit('filename', s)} className="ss-btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} title="Modifier">
                                                                    <i className="fa-solid fa-pen" />
                                                                </button>
                                                                <button onClick={() => removeSuspectName(s.filename)} className="ss-btn-secondary" style={{ padding: '4px 10px', fontSize: 11, color: '#991b1b' }} title="Retirer">
                                                                    <i className="fa-solid fa-xmark" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}

                            {settingsTab === 'paths' && (
                                <>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
                                        Chemins <strong>exacts</strong> testés en listant leur dossier parent. Le <code>/</code> initial est ajouté automatiquement si oublié, le <code>/</code> final est retiré.
                                        Idéal pour cibler un fichier ou dossier suspect dont le nom seul est trop commun
                                        (ex: <code>/dup-installer</code>). Sensibles à la casse.
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                                        <input className="ss-input" type="text" placeholder="/wp-content/plugins/qsdjfq"
                                            value={newPathValue} onChange={e => setNewPathValue(normalizePathAsTyped(e.target.value))}
                                            onKeyDown={e => { if (e.key === 'Enter') addSuspectPath(); }}
                                            style={{ flex: 2, minWidth: 220 }} />
                                        <select className="ss-input" value={newPathCategory}
                                            onChange={e => setNewPathCategory(e.target.value)}
                                            style={{ minWidth: 170, cursor: 'pointer' }}
                                            title="Type d'anomalie quand ce chemin est trouvé">
                                            {SUSPECT_CATEGORY_CHOICES.map(k => (
                                                <option key={k} value={k}>{ISSUE_TYPES[k]?.label || k}</option>
                                            ))}
                                        </select>
                                        <input className="ss-input" type="text" placeholder="Note (optionnel)"
                                            value={newPathNote} onChange={e => setNewPathNote(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') addSuspectPath(); }}
                                            style={{ flex: 1, minWidth: 160 }} />
                                        <button onClick={addSuspectPath} className="ss-btn-primary">
                                            <i className="fa-solid fa-plus" /> Ajouter
                                        </button>
                                    </div>
                                    {pathsError && (
                                        <div style={{ fontSize: 12, color: '#991b1b', background: '#fee2e2', borderLeft: '3px solid #ef4444', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>
                                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} /> {pathsError}
                                        </div>
                                    )}
                                    {suspectPaths.length === 0 ? (
                                        <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '18px 0' }}>
                                            Aucun chemin suspect.
                                        </div>
                                    ) : (
                                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                            {suspectPaths.map((p, i) => {
                                                const catKey = p.category || 'malware';
                                                const catMeta = ISSUE_TYPES[catKey] || ISSUE_TYPES.malware;
                                                const isEditing = editRow && editRow.type === 'path' && editRow.originalKey === p.file_path;
                                                return (
                                                    <div key={p.file_path} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i === suspectPaths.length - 1 ? 'none' : '1px solid #f1f5f9', fontSize: 13, background: isEditing ? '#f1f5f9' : 'transparent' }}>
                                                        <i className="fa-solid fa-folder-tree" style={{ color: catMeta.color, fontSize: 12 }} />
                                                        {isEditing ? renderEditInputs(true) : (
                                                            <>
                                                                <div
                                                                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                                                    onClick={() => beginEdit('path', p)}
                                                                    title="Cliquer pour modifier"
                                                                >
                                                                    <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontSize: 12, wordBreak: 'break-all' }}>{p.file_path}</code>
                                                                    {p.note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{p.note}</div>}
                                                                </div>
                                                                <span style={{ background: '#fff', color: catMeta.color, border: `1px solid ${catMeta.color}44`, padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                    {catMeta.label}
                                                                </span>
                                                                <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                                                    {p.created_at ? new Date(p.created_at).toLocaleDateString('fr-CH') : ''}
                                                                </div>
                                                                <button onClick={() => beginEdit('path', p)} className="ss-btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} title="Modifier">
                                                                    <i className="fa-solid fa-pen" />
                                                                </button>
                                                                <button onClick={() => removeSuspectPath(p.file_path)} className="ss-btn-secondary" style={{ padding: '4px 10px', fontSize: 11, color: '#991b1b' }} title="Retirer">
                                                                    <i className="fa-solid fa-xmark" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}

                            {settingsTab === 'exts' && (
                                <>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
                                        Extensions de fichier à marquer comme suspectes lorsqu'elles apparaissent dans
                                        <code> wp-admin/</code> ou <code>wp-includes/</code>. Le point initial est facultatif
                                        (ajouté automatiquement).
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                                        <input className="ss-input" type="text" placeholder=".zip"
                                            value={newExtValue} onChange={e => setNewExtValue(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') addForbiddenExt(); }}
                                            style={{ flex: 1, minWidth: 140 }} />
                                        <select className="ss-input" value={newExtCategory}
                                            onChange={e => setNewExtCategory(e.target.value)}
                                            style={{ minWidth: 170, cursor: 'pointer' }}
                                            title="Type d'anomalie quand cette extension est trouvée">
                                            {SUSPECT_CATEGORY_CHOICES.map(k => (
                                                <option key={k} value={k}>{ISSUE_TYPES[k]?.label || k}</option>
                                            ))}
                                        </select>
                                        <input className="ss-input" type="text" placeholder="Note (optionnel)"
                                            value={newExtNote} onChange={e => setNewExtNote(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') addForbiddenExt(); }}
                                            style={{ flex: 1, minWidth: 160 }} />
                                        <button onClick={addForbiddenExt} className="ss-btn-primary">
                                            <i className="fa-solid fa-plus" /> Ajouter
                                        </button>
                                    </div>
                                    {extsError && (
                                        <div style={{ fontSize: 12, color: '#991b1b', background: '#fee2e2', borderLeft: '3px solid #ef4444', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>
                                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} /> {extsError}
                                        </div>
                                    )}
                                    {forbiddenExts.length === 0 ? (
                                        <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '18px 0' }}>
                                            Aucune extension dans la liste.
                                        </div>
                                    ) : (
                                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                            {forbiddenExts.map((x, i) => {
                                                const catKey = x.category || 'archive';
                                                const catMeta = ISSUE_TYPES[catKey] || ISSUE_TYPES.archive;
                                                const isEditing = editRow && editRow.type === 'ext' && editRow.originalKey === x.ext;
                                                return (
                                                    <div key={x.ext} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i === forbiddenExts.length - 1 ? 'none' : '1px solid #f1f5f9', fontSize: 13, background: isEditing ? '#f1f5f9' : 'transparent' }}>
                                                        <i className="fa-solid fa-file-zipper" style={{ color: catMeta.color, fontSize: 12 }} />
                                                        {isEditing ? renderEditInputs(true) : (
                                                            <>
                                                                <div
                                                                    style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                                                    onClick={() => beginEdit('ext', x)}
                                                                    title="Cliquer pour modifier"
                                                                >
                                                                    <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{x.ext}</code>
                                                                    {x.note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{x.note}</div>}
                                                                </div>
                                                                <span style={{ background: '#fff', color: catMeta.color, border: `1px solid ${catMeta.color}44`, padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                    {catMeta.label}
                                                                </span>
                                                                <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                                                    {x.created_at ? new Date(x.created_at).toLocaleDateString('fr-CH') : ''}
                                                                </div>
                                                                <button onClick={() => beginEdit('ext', x)} className="ss-btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} title="Modifier">
                                                                    <i className="fa-solid fa-pen" />
                                                                </button>
                                                                <button onClick={() => removeForbiddenExt(x.ext)} className="ss-btn-secondary" style={{ padding: '4px 10px', fontSize: 11, color: '#991b1b' }} title="Retirer">
                                                                    <i className="fa-solid fa-xmark" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Per-site overview ── */}
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
                    <div className="ss-stat-card" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                        <span style={{ fontSize: 28, fontWeight: 800 }}>{tableStats.critical}</span>
                        <span style={{ fontSize: 12, opacity: 0.85 }}>Sites critiques</span>
                    </div>
                    <div className="ss-stat-card green">
                        <span style={{ fontSize: 28, fontWeight: 800 }}>{tableStats.ok}</span>
                        <span style={{ fontSize: 12, opacity: 0.85 }}>Sites OK</span>
                    </div>
                    <div className="ss-stat-card slate">
                        <span style={{ fontSize: 28, fontWeight: 800 }}>{tableStats.issues}</span>
                        <span style={{ fontSize: 12, opacity: 0.85 }}>Anomalies de sécurité</span>
                    </div>
                    <div className="ss-stat-card" style={{ background: 'linear-gradient(135deg, #7c3aed, #5b21b6)' }}>
                        <span style={{ fontSize: 28, fontWeight: 800 }}>{tableStats.withMalware}</span>
                        <span style={{ fontSize: 12, opacity: 0.85 }}>Avec malware / uploads .php</span>
                    </div>
                </div>

                <div className="ss-card">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 14 }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                            <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input
                                className="ss-input"
                                type="text"
                                placeholder="Filtrer par domaine ou client…"
                                value={tableSearch}
                                onChange={e => setTableSearch(e.target.value)}
                                style={{ width: '100%', paddingLeft: 34 }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {[
                                { key: 'all', label: 'Tous', color: '#64748b' },
                                { key: 'critical', label: 'Critique', color: '#ef4444' },
                                { key: 'ok', label: 'OK', color: '#22c55e' }
                            ].map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => setStatusFilter(f.key)}
                                    style={{
                                        padding: '7px 13px',
                                        borderRadius: 20,
                                        border: `1.5px solid ${statusFilter === f.key ? f.color : '#e2e8f0'}`,
                                        background: statusFilter === f.key ? f.color : '#fff',
                                        color: statusFilter === f.key ? '#fff' : '#475569',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        {(() => {
                            const allFiles = availableIssueTypes.reduce((n, t) => n + t.fileCount, 0);
                            const allSites = reports.reduce((n, r) => n + (r.issueTypes?.length > 0 ? 1 : 0), 0);
                            const current = issueTypeFilter === 'all'
                                ? { label: 'Toutes les anomalies', fileCount: allFiles, siteCount: allSites, color: '#334155' }
                                : (availableIssueTypes.find(t => t.key === issueTypeFilter) || { label: issueTypeFilter, fileCount: 0, siteCount: 0, color: '#334155' });
                            return (
                                <div ref={issueDropdownRef} style={{ position: 'relative', minWidth: 260 }}>
                                    <button
                                        type="button"
                                        onClick={() => setIssueDropdownOpen(o => !o)}
                                        className="ss-input"
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            padding: '9px 14px 9px 12px',
                                            background: '#f8fafc'
                                        }}
                                        aria-haspopup="listbox"
                                        aria-expanded={issueDropdownOpen}
                                    >
                                        <i className="fa-solid fa-filter" style={{ color: '#94a3b8', fontSize: 12 }} />
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {current.label}{' '}
                                            <span style={{ color: '#94a3b8', fontSize: 12 }}>
                                                (<strong style={{ color: '#0f172a' }}>{current.fileCount}</strong> fichier{current.fileCount !== 1 ? 's' : ''}
                                                {' · '}<strong style={{ color: '#0f172a' }}>{current.siteCount}</strong> site{current.siteCount !== 1 ? 's' : ''})
                                            </span>
                                        </span>
                                        <i className={`fa-solid ${issueDropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ color: '#94a3b8', fontSize: 11 }} />
                                    </button>
                                    {issueDropdownOpen && (
                                        <div
                                            role="listbox"
                                            style={{
                                                position: 'absolute',
                                                top: 'calc(100% + 4px)',
                                                left: 0,
                                                right: 0,
                                                background: '#fff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: 9,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                                zIndex: 30,
                                                maxHeight: 360,
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {[{ key: 'all', label: 'Toutes les anomalies', fileCount: allFiles, siteCount: allSites, color: '#334155' }, ...availableIssueTypes].map((t, i) => {
                                                const isActive = t.key === issueTypeFilter;
                                                return (
                                                    <div
                                                        key={t.key}
                                                        role="option"
                                                        aria-selected={isActive}
                                                        onClick={() => {
                                                            setIssueTypeFilter(t.key);
                                                            setIssueDropdownOpen(false);
                                                        }}
                                                        style={{
                                                            padding: '9px 14px',
                                                            cursor: 'pointer',
                                                            background: isActive ? '#eff6ff' : '#fff',
                                                            borderBottom: i === availableIssueTypes.length ? 'none' : '1px solid #f1f5f9',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 10,
                                                            fontSize: 13
                                                        }}
                                                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                                                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
                                                    >
                                                        <span style={{
                                                            width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                                                            background: t.color || '#94a3b8'
                                                        }} />
                                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isActive ? '#1e3a8a' : '#0f172a', fontWeight: isActive ? 600 : 500 }}>
                                                            {t.label}
                                                        </span>
                                                        <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                            <strong style={{ color: '#0f172a' }}>{t.fileCount}</strong> fichier{t.fileCount !== 1 ? 's' : ''}
                                                            {' · '}
                                                            <strong style={{ color: '#0f172a' }}>{t.siteCount}</strong> site{t.siteCount !== 1 ? 's' : ''}
                                                        </span>
                                                        {isActive && <i className="fa-solid fa-check" style={{ color: '#3b82f6', fontSize: 11 }} />}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                            {filteredReports.length} / {reports.length}
                        </div>

                    </div>
                    <div className="display-flex mb-10 justify-content-end" style={{ gap: '10px' }}>

                        <button
                            onClick={exportCsv}
                            disabled={filteredReports.length === 0}
                            title="Exporter la liste filtrée au format CSV"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                background: '#107C41',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 9,
                                padding: '10px 18px',
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: filteredReports.length === 0 ? 'not-allowed' : 'pointer',
                                opacity: filteredReports.length === 0 ? 0.55 : 1,
                                boxShadow: '0 1px 2px rgba(16, 124, 65, 0.25)',
                                transition: 'background 0.15s, transform 0.1s'
                            }}
                            onMouseEnter={e => { if (filteredReports.length > 0) e.currentTarget.style.background = '#0E6E3A'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#107C41'; }}
                        >
                            <i className="fa-solid fa-file-csv" />
                            Exporter la liste en CSV
                        </button>
                    </div>
                    {/* Sortable header row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '100px 260px 1fr 120px 100px',
                        gap: 10,
                        padding: '10px 14px',
                        background: '#f8fafc',
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: 0.3
                    }}>
                        {[
                            { key: 'status', label: 'Statut', align: 'left' },
                            { key: 'site', label: 'Site', align: 'left' },
                            { key: 'issues', label: 'Anomalies', align: 'left' },
                            { key: 'lastScan', label: 'Dernier scan', align: 'left' },
                            { key: null, label: '', align: 'right' }
                        ].map((c, i) => c.key ? (
                            <button
                                key={c.key}
                                onClick={() => toggleSort(c.key)}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    textAlign: c.align, color: sortKey === c.key ? '#3b82f6' : '#64748b',
                                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
                                    padding: 0
                                }}
                            >
                                {c.label} {sortKey === c.key && (sortAsc ? '▲' : '▼')}
                            </button>
                        ) : <div key={`spacer-${i}`} />)}
                    </div>

                    {/* Rows */}
                    {filteredReports.length === 0 ? (
                        <div style={{ padding: '30px 15px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                            Aucun site ne correspond aux filtres.
                        </div>
                    ) : filteredReports.map(r => {
                        const host = (r.website || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
                        const firm = sitesMap[r.website]?.firm || '';
                        const c = r.counts || {};
                        const isOpen = !!openRows[r.website];
                        const isScanning = (loading && scanningUrlRef.current === r.website)
                            || (snapshot?.single?.siteUrl === r.website)
                            || !!snapshot?.progress?.[r.website];
                        const badge = r.status === 'critical' ? { bg: '#ef4444', label: 'CRITIQUE' }
                            : (r.status === 'warning' || r.status === 'ok') ? { bg: '#22c55e', label: 'OK' }
                                : { bg: '#94a3b8', label: (r.status || '—').toUpperCase() };

                        const section = (title, items, color) => {
                            if (!items || items.length === 0) return null;
                            return (
                                <div style={{ marginTop: 10, borderLeft: `3px solid ${color}`, paddingLeft: 10 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                                        {title} <span style={{ color: '#94a3b8', fontWeight: 500 }}>({items.length})</span>
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
                                        {items.slice(0, 25).map((f, i) => (
                                            <li key={i} style={{ wordBreak: 'break-all' }}>
                                                <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>{f}</code>
                                            </li>
                                        ))}
                                        {items.length > 25 && <li><em style={{ color: '#94a3b8' }}>… et {items.length - 25} de plus</em></li>}
                                    </ul>
                                </div>
                            );
                        };

                        return (
                            <div key={r.website} className="ss-row" style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <div
                                    onClick={() => toggleRow(r.website)}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '100px 260px 1fr 120px 100px',
                                        gap: 10,
                                        padding: '12px 14px',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s',
                                        background: isOpen ? '#f8fafc' : 'transparent'
                                    }}
                                    onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#fafafa'; }}
                                    onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
                                >
                                    <span style={{
                                        background: badge.bg, color: '#fff',
                                        padding: '4px 10px', borderRadius: 20,
                                        fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
                                        width: 'fit-content'
                                    }}>{badge.label}</span>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {host}
                                        </div>
                                        {firm && <div style={{ fontSize: 11, color: '#94a3b8' }}>{firm}</div>}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#475569', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {c.malware > 0 && <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>🦠 {c.malware}</span>}
                                        {c.uploadsPhp > 0 && <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>📄 {c.uploadsPhp}</span>}
                                        {c.modified > 0 && <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>✎ {c.modified}</span>}
                                        {c.added > 0 && <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>+{c.added}</span>}
                                        {c.security === 0 && <span style={{ color: '#94a3b8' }}>—</span>}
                                        {(r.issueTypes || []).map(t => (
                                            <span
                                                key={t}
                                                title={ISSUE_TYPES[t]?.label || t}
                                                onClick={(e) => { e.stopPropagation(); setIssueTypeFilter(t); }}
                                                style={{
                                                    background: '#fff',
                                                    color: ISSUE_TYPES[t]?.color || '#334155',
                                                    border: `1px solid ${ISSUE_TYPES[t]?.color || '#e2e8f0'}44`,
                                                    padding: '2px 7px',
                                                    borderRadius: 10,
                                                    fontWeight: 500,
                                                    fontSize: 10,
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {ISSUE_TYPES[t]?.label || t}
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#64748b' }}>{formatScanDate(r.last_scan)}</div>
                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={() => !isScanning && startScanFromTable(r.website)}
                                            disabled={isScanning}
                                            className="ss-btn-secondary"
                                            style={{ padding: '5px 10px', fontSize: 11, opacity: isScanning ? 0.7 : 1 }}
                                            title={isScanning ? 'Scan en cours…' : 'Relancer un scan sur ce site'}
                                        >
                                            <i className={`fa-solid ${isScanning ? 'fa-circle-notch fa-spin' : 'fa-rotate'}`} />
                                        </button>
                                        <NavLink
                                            to={`/rapport-securite/${encodeURIComponent(r.website)}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="ss-btn-secondary"
                                            style={{ padding: '5px 10px', fontSize: 11, textDecoration: 'none' }}
                                            title="Ouvrir le rapport complet"
                                        >
                                            <i className="fa-solid fa-up-right-from-square" />
                                        </NavLink>
                                    </div>
                                </div>
                                {isOpen && (
                                    <div style={{ padding: '4px 14px 16px 14px', background: '#f8fafc' }}>
                                        {isScanning && (() => {
                                            const rowProgress = snapshot?.progress?.[r.website];
                                            return (
                                                <div style={{
                                                    background: '#fff',
                                                    border: '1px solid #bfdbfe',
                                                    borderLeft: '4px solid #3b82f6',
                                                    borderRadius: 8,
                                                    padding: '10px 14px',
                                                    margin: '6px 0 10px',
                                                    fontSize: 12,
                                                    color: '#1e3a8a'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: rowProgress ? 4 : 0 }}>
                                                        <i className="fa-solid fa-circle-notch fa-spin" style={{ color: '#3b82f6' }} />
                                                        Scan en cours sur ce site…
                                                    </div>
                                                    {rowProgress && (
                                                        <>
                                                            <div style={{ fontSize: 12, color: '#334155' }}>
                                                                <i className={`fa-solid ${PHASE_META[rowProgress.phase]?.icon || 'fa-gears'}`} style={{ color: '#3b82f6', marginRight: 6 }} />
                                                                <strong>{PHASE_META[rowProgress.phase]?.label || rowProgress.phase || '…'}</strong>
                                                                {rowProgress.message && <> — {rowProgress.message}</>}
                                                            </div>
                                                            {rowProgress.path && (
                                                                <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 4 }}>
                                                                    {rowProgress.path}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        {c.security === 0 && !isScanning && (
                                            <div style={{ fontSize: 12, color: '#64748b', padding: '8px 0' }}>
                                                <i className="fa-solid fa-check-circle" style={{ color: '#22c55e', marginRight: 6 }} />
                                                Aucune anomalie de sécurité détectée lors du dernier scan.
                                            </div>
                                        )}
                                        {section('Malware & code suspect', r.parsedDetails.malware, '#ef4444')}
                                        {section('.php dans wp-content/uploads', r.parsedDetails.uploadsPhp, '#ef4444')}
                                        {section('Fichiers modifiés', r.parsedDetails.modified, '#f59e0b')}
                                        {section('Fichiers ajoutés', r.parsedDetails.added, '#22c55e')}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Info footer */}
                <div className="ss-card" style={{ background: '#fcfcfc' }}>
                    <h4 style={{ margin: '0 0 10px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <i className="fa-solid fa-microscope" style={{ color: '#1dbab0' }} />
                        Ce que vérifie un scan unitaire
                    </h4>
                    <ul style={{ fontSize: 13, color: '#64748b', paddingLeft: 20, lineHeight: 1.8, margin: 0 }}>
                        <li>Détection de la version WordPress installée</li>
                        <li>Lecture récursive de <code>wp-admin/</code> et <code>wp-includes/</code></li>
                        <li>Chemins malveillants connus (incluant <code>/.well-known</code> en racine)</li>
                        <li>Recherche récursive du dossier <code>.well-known</code> dans <code>wp-admin</code>, <code>wp-content</code> et <code>wp-content/uploads</code></li>
                        <li>Détection de fichiers <code>.php</code> dans <code>wp-content/uploads</code> (récursive — aucun ne devrait exister)</li>
                        <li>Vérification MD5 de fichiers core critiques (index, functions, load, post)</li>
                    </ul>
                </div>
            </div>
        </Fragment>
    );
};

export default SitesSecurite;
